import React, { forwardRef } from "react";
import { Box, Typography } from "@mui/material";
import { TextInputContainer, TextInputField, Label } from "./TextInput.styled";

interface TextInputProps {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onFocus?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  disabled?: boolean;
  readOnly?: boolean;
  fullWidth?: boolean;
  width?: string | number;
  type?: string;
  name?: string;
  id?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  height?: string | number;
  maxLength?: number;
}

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      placeholder,
      value,
      onChange,
      onFocus,
      onBlur,
      disabled = false,
      readOnly = false,
      fullWidth = true,
      width,
      type = "text",
      name,
      id,
      error = false,
      helperText,
      required = false,
      multiline = false,
      rows,
      height = "auto",
      maxLength,
    },
    ref,
  ) => {
    return (
      <Box sx={{ width: fullWidth ? "100%" : width }}>
        {label && (
          <Label>
            {label}
            {required && "*"}
          </Label>
        )}
        <TextInputContainer
          sx={{ height: multiline ? height : "48px" }}
          error={error || undefined}
          multiline={multiline}
          readOnly={readOnly}
        >
          <TextInputField
            ref={ref}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            disabled={disabled}
            readOnly={readOnly}
            fullWidth
            type={type}
            name={name}
            id={id}
            multiline={multiline}
            rows={rows}
            inputProps={{ maxLength, readOnly }}
          />
        </TextInputContainer>
        {helperText && (
          <Typography
            variant="caption"
            sx={{
              color: error ? "status.error" : "text.secondary",
              fontSize: "12px",
              mt: 0.5,
              display: "block",
            }}
          >
            {helperText}
          </Typography>
        )}
      </Box>
    );
  },
);

TextInput.displayName = "TextInput";

export default TextInput;
